import styled from 'styled-components';
import { LauncherElementData, useLauncherElementsStore } from '../../../hooks/stores/launcherElementsStore';
import { UIData, useUIStore } from '../../../hooks/stores/uiStore';
import { Dimensions } from './Dimensions';
import { MarginArea } from './MarginArea';
import { PaddingArea } from './PaddingArea';
import { Corner, Fill, Horizontal, Vertical } from './SubHandles';
import { useScene } from '../../../context/sceneContext';

export const ElementHandle = () => {
  const scene = useScene();
  const [UIData, setUIData] = useUIStore<UIData>({ selector: ['selectedElementIDs', 'elementHandle'] });
  const [launcherElementsData] = useLauncherElementsStore<Record<string, LauncherElementData>>();
  const data = UIData.elementHandle;

  const firstActiveElementID = UIData.selectedElementIDs[0];

  const type = UIData.selectedElementIDs
    .map((id) => launcherElementsData[id]?.type)
    .every((val, i, arr) => val === arr[0])
    ? firstActiveElementID === undefined ? null : launcherElementsData[firstActiveElementID]?.type
    : null;

  return (
    <HandleContainer
      id="handle"
      $isVisible={data.isVisible}
      style={{
        left: data.pos.x,
        top: data.pos.y,
        width: data.width,
        height: data.height,
      }}
      onDoubleClick={() => {
        if (type === 'text' || type === 'link') {
          scene.startEditingElement(firstActiveElementID);
        }
      }}
    >
      <InnerContainer>
        <Fill
          launcher-element-type={type}
          onMouseDown={(e) => {
            if (e.button !== 0) return;
            setUIData({
              elementHandle: {
                ...UIData.elementHandle,
                isDragging: true,
                isResizing: false,
                isDraggingX: true,
                isDraggingY: true,
                isResizingX: false,
                isResizingY: false,
                isResizingReversedX: false,
                isResizingReversedY: false,
              },
            });
          }}
        />

        {/* Top */}
        <Horizontal
          onMouseDown={() =>
            setUIData({
              elementHandle: {
                ...UIData.elementHandle,
                isDragging: true,
                isResizing: true,
                isDraggingX: false,
                isDraggingY: true,
                isResizingX: false,
                isResizingY: true,
                isResizingReversedX: false,
                isResizingReversedY: true,
              },
            })
          }
          style={{ top: 0, translate: '0 -50%', cursor: 'n-resize' }}
        />
        {/* Right */}
        <Vertical
          onMouseDown={() =>
            setUIData({
              elementHandle: {
                ...UIData.elementHandle,
                isDragging: false,
                isResizing: true,
                isDraggingX: false,
                isDraggingY: false,
                isResizingX: true,
                isResizingY: false,
                isResizingReversedX: false,
                isResizingReversedY: false,
              },
            })
          }
          style={{ right: 0, translate: '50% 0', cursor: 'e-resize' }}
        />
        {/* Bottom */}
        <Horizontal
          onMouseDown={() =>
            setUIData({
              elementHandle: {
                ...UIData.elementHandle,
                isDragging: false,
                isResizing: true,
                isDraggingX: false,
                isDraggingY: false,
                isResizingX: false,
                isResizingY: true,
                isResizingReversedX: false,
                isResizingReversedY: false,
              },
            })
          }
          style={{ bottom: 0, translate: '0 50%', cursor: 's-resize' }}
        />
        {/* Left */}
        <Vertical
          onMouseDown={() =>
            setUIData({
              elementHandle: {
                ...UIData.elementHandle,
                isDragging: true,
                isResizing: true,
                isDraggingX: true,
                isDraggingY: false,
                isResizingX: true,
                isResizingY: false,
                isResizingReversedX: true,
                isResizingReversedY: false,
              },
            })
          }
          style={{ left: 0, translate: '-50% 0', cursor: 'w-resize' }}
        />
        {/* TopLeft */}
        <Corner
          onMouseDown={() =>
            setUIData({
              elementHandle: {
                ...UIData.elementHandle,
                isDragging: true,
                isResizing: true,
                isDraggingX: true,
                isDraggingY: true,
                isResizingX: true,
                isResizingY: true,
                isResizingReversedX: true,
                isResizingReversedY: true,
              },
            })
          }
          style={{
            top: 0,
            left: 0,
            translate: '-50% -50%',
            cursor: 'nw-resize',
          }}
        />
        {/* TopRight */}
        <Corner
          onMouseDown={() =>
            setUIData({
              elementHandle: {
                ...UIData.elementHandle,
                isDragging: true,
                isResizing: true,
                isDraggingX: false,
                isDraggingY: true,
                isResizingX: true,
                isResizingY: true,
                isResizingReversedX: false,
                isResizingReversedY: true,
              },
            })
          }
          style={{
            top: 0,
            right: 0,
            translate: '50% -50%',
            cursor: 'ne-resize',
          }}
        />
        {/* /BottomLeft */}
        <Corner
          onMouseDown={() =>
            setUIData({
              elementHandle: {
                ...UIData.elementHandle,
                isDragging: true,
                isResizing: true,
                isDraggingX: true,
                isDraggingY: false,
                isResizingX: true,
                isResizingY: true,
                isResizingReversedX: true,
                isResizingReversedY: false,
              },
            })
          }
          style={{
            bottom: 0,
            left: 0,
            translate: '-50% 50%',
            cursor: 'sw-resize',
          }}
        />
        {/* BottomRight */}
        <Corner
          onMouseDown={() =>
            setUIData({
              elementHandle: {
                ...UIData.elementHandle,
                isDragging: false,
                isResizing: true,
                isDraggingX: false,
                isDraggingY: false,
                isResizingX: true,
                isResizingY: true,
                isResizingReversedX: false,
                isResizingReversedY: false,
              },
            })
          }
          style={{
            bottom: 0,
            right: 0,
            translate: '50% 50%',
            cursor: 'se-resize',
          }}
        />

        <PaddingArea
          elementID={
            UIData.selectedElementIDs.length === 1
              ? UIData.selectedElementIDs[0]
              : null
          }
        />
        <MarginArea
          elementID={
            UIData.selectedElementIDs.length === 1
              ? UIData.selectedElementIDs[0]
              : null
          }
        />
      </InnerContainer>
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <Dimensions>
          {Math.round(data.width) + ' x ' + Math.round(data.height)}
        </Dimensions>
      </div>
    </HandleContainer>
  );
};

const HandleContainer = styled.div<{
  $isVisible?: boolean;
}>`
  pointer-events: all;
  box-sizing: border-box;
  position: absolute;

  transition: opacity var(--transition-2);
  ${({ $isVisible }) => !$isVisible && 'opacity: 0; pointer-events: none;'}
`;

const InnerContainer = styled.div<{ $isDragging?: boolean }>`
  cursor: grab;
  box-sizing: border-box;
  width: 100%;
  height: 100%;

  display: flex;
  justify-content: center;
  align-items: center;

  outline: 1px solid #eee;
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    outline: 1px dashed #333;
    pointer-events: none;
  }

  ${({ $isDragging }) => $isDragging && `cursor: grabbing; opacity: 0`}
`;
